import {
  alpha,
  Box,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  IconButton,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ExportCsvButton from "../../components/ExportCsvButton";
import RowPerPage from "../../components/RowPerPage";
import Pagination from "../../components/Pagination";
import DropdownFilter from "../../components/ui/DropdownFilter";
import SearchInput from "../../components/ui/SearchInput";
import { useUsersPaginated } from "../../features/users/useUsers";
import { useLocationOverview } from "../../features/location/useLocation";
import TableSkeleton from "../../components/TableSkeleton";


const columns = [
  { label: "ID", key: "id" },
  { label: "Name", key: "name" },
  { label: "Email", key: "email" },
  { label: "Status", key: "status" },
  { label: "Country", key: "country" },
  { label: "IP Address", key: "ipAddress" },
  { label: "Device", key: "device" },
  { label: "Registration Date", key: "regDate" },
  { label: "Subscriptions", key: "subscriptionList" },
  { label: "Storage Used", key: "storageUsed" },
];

const getFlagUrl = (code) => {
  if (!code) return "";
  return `https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.5.0/flags/4x3/${code.toLowerCase()}.svg`;
};

// Subscription icons path mapping
const subscriptionIcons = {
  Pro: "/images/karan13.png",
  Advanced: "/images/rishi.png",
  Basic: "/images/karan6.png",
  Pro1: "/images/karan2.png",
  Pro2: "/images/karan12.png",
};

export default function CountryDashboard() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [selectedMonth, setSelectedMonth] = useState("All Time");
  const months = ["All Time", "This month", "Last month", "Last 3 months"];

  const [selectedCountry, setSelectedCountry] = useState("All Countries");

  const [search, setSearch] = useState("");

  const [subscriptionAnchorEl, setSubscriptionAnchorEl] = useState(null);
  const activeSubscriptions = [];
  const [isHovering, setIsHovering] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Calculate date filters
  const dateFilters = useMemo(() => {
    const now = new Date();
    let from = null;
    let to = new Date();

    if (selectedMonth === "This month") {
      from = new Date(now.getFullYear(), now.getMonth(), 1);
    } else if (selectedMonth === "Last month") {
      from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      to = new Date(now.getFullYear(), now.getMonth(), 0);
    } else if (selectedMonth === "Last 3 months") {
      from = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    } else if (selectedMonth === "All Time") {
      from = null;
    }
    return { createdFrom: from, createdTo: to };
  }, [selectedMonth]);

  const { data: userData, isLoading } = useUsersPaginated({
    page,
    rowsPerPage,
    searchTerm: search,
    filters: {
      country:
        selectedCountry === "All Countries" ? undefined : selectedCountry,
      ...dateFilters,
    },
  });

  // API Calls
  const { data: locationOverview } = useLocationOverview();
  const availableCountries = locationOverview?.countryData || [];

  const pageData = userData?.items || [];
  const total = userData?.total || 0;

  // Handle page changes for pagination
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleSubscriptionPopoverClose = () => {
    setTimeout(() => {
      if (!isHovering) {
        setSubscriptionAnchorEl(null);
      }
    }, 100);
  };

  const handlePopoverMouseEnter = () => {
    setIsHovering(true);
  };

  const handlePopoverMouseLeave = () => {
    setIsHovering(false);
    handleSubscriptionPopoverClose();
    // handleClose(); // Ensure country popover also closes if needed, though they are separate
  };

  const isDarkMode = theme.palette.mode === "dark";

  const getInitials = (name) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((part) => part[0] || "")
      .join("");
  };
  return (
    <Box
      sx={{
        bgcolor: "background.default",
        color: "text.primary",
        minHeight: "100vh",
        // p: 3,
        boxShadow: "none",
        border: `1px solid ${theme.palette.divider}`,
        p: 3,
        borderRadius: 2,
      }}
    >
      {/* Title + Search + Export */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box
          display="flex"
          justifyContent={"center"}
          alignItems="center"
          gap={4}
        >
          <Typography variant="h4" fontWeight="bold">
            {selectedCountry !== "All Countries"
              ? selectedCountry
              : "Country List"}
          </Typography>
          <RowPerPage
            rowsPerPage={rowsPerPage}
            onRowsChange={(n) => {
              setRowsPerPage(n);
              setPage(0);
            }}
            options={[10, 20, 30, 40]}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <SearchInput
            
            label={"Search by name, email, IP, or location"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <ExportCsvButton
            filename="view-details.csv"
            data={pageData}
            columns={columns}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 2,
          mb: 2,
        }}
      >
        <DropdownFilter
          title="Showing"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          options={months}
        />

        {/* Country Dropdown */}
        <DropdownFilter
          title="Country"
          value={selectedCountry}
          onChange={(e) => {
            setSelectedCountry(e.target.value);
            setPage(0); // Reset to first page when changing country
          }}
          options={[
            "All Countries",
            ...(Array.isArray(availableCountries)
              ? availableCountries.map((c) => c.name)
              : []),
          ]}
        />
      </Box>

      {/* Table */}
      <Box>
        <TableContainer
          sx={{
            p: 2,
            borderRadius: 2,
            boxShadow: "none",
            border: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(2),
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: isDarkMode
                    ? "#171717"
                    : "background.neutral",
                }}
              >
                {[
                  "Client",
                  "Country",
                  "Email",
                  "Reg. Date",
                  // "Device",
                  "IP Address",
                  // "Subscriptions",
                  // "Storage Usage",
                  "Status",
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      fontSize: "1.2rem",
                      color: theme.palette.text.primary,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      fontWeight: "bold",
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <TableSkeleton
                      columns={10}
                      animation="wave"
                      variant="rectangular"
                      width="100%"
                      height={40}
                      sx={{ bgcolor: alpha(theme.palette.action.hover, 0.1) }}
                    />
                  </TableCell>
                </TableRow>
              ) : Array.isArray(pageData) && pageData.length > 0 ? (
                pageData.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      {c.profilePicture ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            component="img"
                            src={c.profilePicture}
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                          {c.name}
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/users?userId=${c.id || c._id}`)}
                            sx={{ ml: 0.5, color: theme.palette.primary.main }}
                            title="View User Details"
                          >
                            <ArrowForwardIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              bgcolor: isDarkMode ? "#333" : "#e0e0e0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: isDarkMode ? "#fff" : "#000",
                              fontSize: 12,
                            }}
                          >
                            {getInitials(c.firstName + " " + c.lastName)}
                          </Box>
                          {c.name}
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/users?userId=${c.id || c._id}`)}
                            sx={{ ml: 0.5, color: theme.palette.primary.main }}
                            title="View User Details"
                          >
                            <ArrowForwardIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>

                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          component="img"
                          src={getFlagUrl(c.country_code)}
                          alt={`${c.country} Flag`}
                          sx={{
                            width: 24,
                            height: 16,
                            objectFit: "cover",
                            borderRadius: "2px",
                          }}
                        />
                        {c.country || "-"}
                      </Box>
                    </TableCell>

                    <TableCell>{c.email}</TableCell>
                    <TableCell>{c.createdAt}</TableCell>
                    {/* <TableCell>{c.device || "-"}</TableCell> */}
                    <TableCell>
                      {c?.registerIpAddress
                        ? c.registerIpAddress === "::1"
                          ? "DEVELOPMENT_MODE"
                          : c.registerIpAddress.replace(/^::ffff:/, "")
                        : "Not Provided"}
                    </TableCell>

                    {/* <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                      >
                        {c?.subscriptions?.map((sub, idx) => (
                          <Box
                            key={idx}
                            component="img"
                            src={subscriptionIcons[sub]}
                            alt={sub}
                            sx={{
                              width: 24,
                              height: 24,
                              objectFit: "contain",
                            }}
                          />
                        ))}
                        <Typography variant="caption" sx={{ ml: 0.5 }}>
                          +10
                        </Typography>
                      </Box>
                    </TableCell> */}

                    {/* <TableCell>
                      <Storage user={c} />
                    </TableCell> */}

                    <TableCell>
                      <Box
                        sx={{
                          color:
                            c.isVerified === true
                              ? "success.main"
                              : "error.main",
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                        }}
                      >
                        {c.isVerified === true ? "Verified" : "Unverified"}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, total)} of {total} users
          </Typography>
          <Pagination
            page={page}
            count={total}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
          />
        </Box>
      </Box>

      {/* Subscription Popover */}
      <Popover
        open={Boolean(subscriptionAnchorEl)}
        anchorEl={subscriptionAnchorEl}
        onClose={handleSubscriptionPopoverClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ pointerEvents: "auto" }}
        props={{
          sx: {
            p: 1.5,
            width: 220,
            bgcolor: "#1a1a1a",
            color: "white",
            borderRadius: 1,
            zIndex: 9999,
            boxShadow: "0 4px 20px rgba(0,0,0,0.7)",
          },
          onMouseEnter: handlePopoverMouseEnter,
          onMouseLeave: handlePopoverMouseLeave,
        }}
        slotProps={{
          root: {
            sx: {
              zIndex: 9999,
            },
          },
        }}
        style={{ zIndex: 9999 }}
        disableRestoreFocus
        disablePortal={false}
        keepMounted
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: "#ffffff",
              pb: 1,
              borderBottom: "1px solid #333",
              mb: 1,
            }}
          >
            Subscriptions
          </Typography>

          {activeSubscriptions.map((sub, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 0.7,
                borderBottom:
                  idx < activeSubscriptions.length - 1
                    ? "1px solid rgba(255,255,255,0.1)"
                    : "none",
              }}
            >
              <Box
                component="img"
                src={subscriptionIcons[sub]}
                alt={sub}
                sx={{
                  width: 20,
                  height: 20,
                  objectFit: "contain",
                }}
              />
              <Box sx={{ flexGrow: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "#ffffff", fontWeight: 500 }}
                >
                  {sub}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: "#2c96ff" }}>
                {idx === 0
                  ? "31.94 GB used"
                  : idx === 1
                  ? "13.75 GB used"
                  : "8.32 GB used"}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </Box>
  );
}
